/**
 * TAILWIND INPUT KOMPONENTE
 *
 * Wiederverwendbares Eingabefeld mit modernem outline-Ansatz.
 * Konsistentes Styling mit TWSelect und TWSearch.
 *
 * Props:
 *   value        - string | number
 *   onChange      - (value: string) => void
 *   label?        - string, Label über dem Input
 *   placeholder?  - string
 *   type?         - HTML input type (default: "text")
 *   icon?         - ReactNode, Icon links im Input
 *   error?        - string | boolean, Fehler-Zustand (string = Fehlermeldung)
 *   disabled?     - boolean
 *   autoFocus?    - boolean
 *   className?    - zusätzliche Klassen für den äußeren Container
 *   inputClassName? - zusätzliche Klassen für das Input-Element
 *   id?           - string, HTML-ID
 *   name?         - string, HTML-Name
 *   required?     - boolean
 *   autoComplete? - string
 *   maxLength?    - number
 *   min?          - string | number
 *   max?          - string | number
 *   step?         - string | number
 *   rightElement?  - ReactNode, Element rechts im Input (z.B. Passwort-Toggle)
 *   onKeyDown?    - KeyboardEventHandler
 *   onBlur?       - FocusEventHandler
 *   inputRef?     - React.Ref<HTMLInputElement>
 *
 * Usage:
 *   <TWInput
 *     value={email}
 *     onChange={setEmail}
 *     label="E-Mail"
 *     type="email"
 *     placeholder="name@beispiel.de"
 *     icon={<Mail className="w-4 h-4" />}
 *     error={errors.email}
 *   />
 *
 *   <TWInput
 *     value={name}
 *     onChange={setName}
 *     placeholder="Dein Name"
 *   />
 */

import clsx from 'clsx';

interface TWInputProps {
  value: string | number;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  type?: React.HTMLInputTypeAttribute;
  icon?: React.ReactNode;
  rightElement?: React.ReactNode;
  error?: string | boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  inputClassName?: string;
  id?: string;
  name?: string;
  required?: boolean;
  autoComplete?: string;
  maxLength?: number;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  inputRef?: React.Ref<HTMLInputElement>;
}

export const TWInput: React.FC<TWInputProps> = ({
  value,
  onChange,
  label,
  placeholder,
  type = 'text',
  icon,
  rightElement,
  error,
  disabled = false,
  autoFocus = false,
  className,
  inputClassName,
  id,
  name,
  required,
  autoComplete,
  maxLength,
  min,
  max,
  step,
  onKeyDown,
  onBlur,
  onFocus,
  inputRef,
}) => {
  const hasError = !!error;
  const errorMessage = typeof error === 'string' ? error : undefined;

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
        >
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
            {icon}
          </span>
        )}
        <input
          ref={inputRef}
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          required={required}
          autoComplete={autoComplete}
          maxLength={maxLength}
          min={min}
          max={max}
          step={step}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onFocus={onFocus}
          className={clsx(
            'block w-full rounded-lg text-base sm:text-sm',
            'bg-gray-100 dark:bg-white/5',
            'text-gray-900 dark:text-white',
            'placeholder:text-gray-400 dark:placeholder:text-gray-500',
            'outline-1 -outline-offset-1',
            'transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'py-1.5',
            icon ? 'pl-10' : 'pl-3',
            rightElement ? 'pr-10' : 'pr-3',
            // Outline-Farbe: Normal vs. Fehler
            hasError
              ? 'outline-red-500 focus:outline-2 focus:-outline-offset-2 focus:outline-red-500'
              : 'outline-gray-300 dark:outline-white/10 focus:outline-2 focus:-outline-offset-2 focus:outline-brand',
            inputClassName
          )}
        />
        {rightElement && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {rightElement}
          </div>
        )}
      </div>
      {errorMessage && (
        <p className="mt-1 text-xs text-red-500">{errorMessage}</p>
      )}
    </div>
  );
};
